import React from 'react';
import { styled, TextTag } from '@volkswagen-onehub/components-core';
import { Breakpoints } from '@volkswagen-onehub/components-core';
import { Icon } from '../Icon';
import { DisclaimerFeatureModel } from '../../fetch-data/graphql/types';
import { renderMarkdown } from '../Section/features/ContentFeature/overrides/markdown';
import { TextBox } from '../Box';
import {
  TextAppearance,
  TextWeight,
  TextLineHeight,
  TextSize,
} from '@vw-marketing/us-components';
import { useDisclaimer } from './DisclaimerContext';

const ModalContainer = styled.div<{ $isOpen: boolean }>`
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  word-break: normal;
  flex-direction: row;
  justify-content: space-between;
  position: fixed;
  color: rgb(255, 255, 255);
  background-color: rgba(0, 0, 0, 0.8);
  bottom: 0px;
  left: 0px;
  right: 0px;
  padding-top: 20px;
  padding-bottom: 10px;
  opacity: 0.9;
  z-index: 1000;
  transition: 0.1s;
  margin: 0px auto;
  width: 83.3333vw;

  @media screen and (min-width: 961px) and (max-width: 1600px) {
    padding-left: 20px;
    padding-right: 20px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  padding-left: 20px;
  padding-right: 20px;

  @media screen and (min-width: 961px) {
    padding-left: 20px;
    padding-right: 20px;
  }
`;

const NumberIndicator = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 0px;
  flex-shrink: 0;
`;

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  color: rgb(255, 255, 255) !important;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  max-height: 180px;

  @media (min-width: ${Breakpoints.b560}px) {
    max-height: 300px;
  }

  @media (min-width: ${Breakpoints.b1280}px) {
    font-size: 16px;
    line-height: 24px;
    max-height: 400px;
  }

  * {
    box-sizing: border-box;
    color: rgb(255, 255, 255) !important;
  }

  /* Override any nested color styles */
  span,
  div,
  p,
  ul,
  ol,
  li,
  a {
    color: rgb(255, 255, 255) !important;
  }

  /* Ensure SVG icons are white */
  svg {
    fill: rgb(255, 255, 255) !important;
    color: rgb(255, 255, 255) !important;
  }

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }
`;

const CloseButtonWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-shrink: 0;
  padding-right: 20px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(255, 255, 255);
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }

  &:focus {
    outline: 2px solid rgb(255, 255, 255);
    outline-offset: 2px;
  }
`;

interface VWDisclaimerModalProps {
  isOpen: boolean;
  disclaimer: DisclaimerFeatureModel | null;
  onClose: () => void;
  disclaimerNumber?: number;
}

export const VWDisclaimerModal: React.FC<VWDisclaimerModalProps> = ({
  isOpen,
  disclaimer,
  onClose,
}) => {
  const { activeDisclaimerDisplayName } = useDisclaimer();

  if (!disclaimer || !isOpen) return null;

  const disclaimerContent = disclaimer.disclaimerContent?.html;

  if (!disclaimerContent) return null;

  const displayName = activeDisclaimerDisplayName || disclaimer.disclaimerId;

  return (
    <ModalContainer $isOpen={isOpen} tabIndex={0}>
      <ContentWrapper>
        <NumberIndicator>
          <TextBox
            appearance={TextAppearance.copy200}
            tag={TextTag.p}
            style={{
              [Breakpoints.default]: {
                fontSize: TextSize.TextSize16,
                lineHeight: TextLineHeight.TextLineHeight28,
                fontWeight: TextWeight.regular,
              },
              [Breakpoints.b1920]: {
                fontSize: TextSize.TextSize18,
                lineHeight: TextLineHeight.TextLineHeight32,
              },
            }}
            wrapperStyle={{
              margin: 0,
            }}
          >
            {displayName}.
          </TextBox>
        </NumberIndicator>
        <ContentArea>
          <span style={{ overflow: 'visible' }}>
            <span>
              {renderMarkdown({
                text: disclaimerContent,
              })}
            </span>
          </span>
        </ContentArea>
      </ContentWrapper>
      <CloseButtonWrapper>
        <CloseButton
          onClick={onClose}
          aria-label='Close'
          role='button'
          tabIndex={0}
        >
          <Icon
            name='Close'
            ignoreDefaultColor={true}
            style={{ color: '#FFFFFF' }}
          />
        </CloseButton>
      </CloseButtonWrapper>
    </ModalContainer>
  );
};
